/**
 * Generic Schema Migration Executor — Applies diff to BlockSuite database.
 *
 * sl-oll9.10: Extracted from elster-schema-executor.ts.
 * All changes are atomic via Y.js store transact().
 *
 * SOLID:
 * - S: Only migration execution, no UI or diffing
 * - D: Depends on DatabaseBlockDataSource API
 */

import type { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';

import type { SchemaDiff } from './schema-diff';
import { createSelectColumnOptions } from './select-column-options';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Map diff dbType to BlockSuite property type string */
const DB_TYPE_TO_PROPERTY_TYPE: Record<string, string> = {
  number: 'number',
  checkbox: 'checkbox',
  date: 'date',
  select: 'select',
  'rich-text': 'text',
};

// ─── Execute Migration ──────────────────────────────────────────────────────

/**
 * Apply schema migration diff to a BlockSuite database.
 *
 * @param dataSource - DatabaseBlockDataSource for the target database
 * @param diff - Computed schema diff
 * @param onComplete - Callback with the new version string (to update block props)
 */
export function executeSchemaMigration(
  dataSource: DatabaseBlockDataSource,
  diff: SchemaDiff,
  onComplete: (newVersion: string) => void
): void {
  // 1. Add new columns
  for (const added of diff.added) {
    const propertyType = DB_TYPE_TO_PROPERTY_TYPE[added.dbType];
    if (!propertyType) continue;

    const propId = dataSource.propertyAdd('end', {
      type: propertyType,
      name: added.columnName,
    });

    // For select columns, set options from enum values
    if (propId && added.dbType === 'select' && added.enumValues) {
      dataSource.propertyDataSet(propId, {
        options: createSelectColumnOptions(Object.values(added.enumValues)),
      });
    }
  }

  // 2. Delete removed columns
  for (const removed of diff.removed) {
    dataSource.propertyDelete(removed.columnId);
  }

  // 3. Signal completion with new version
  onComplete(diff.targetVersion);
}
